import { AnalyticsService } from 'base/analytics';
import { UnreachableError } from 'base/helpers';
import { Button } from 'base/ui/button';
import { ColoredIcon } from 'base/ui/colored_icon';
import { IconStars } from 'base/ui/icons';
import { MenuDisplayOptions, useAnchoredMenu } from 'base/ui/menu';
import { menuItem } from 'base/ui/menu_model';
import { ClickInTextarea, ClickInTextareaRef } from 'base/ui/textarea';
import {
  isReplaceableTextNode,
  textContentsOf,
  TextReplacementType,
} from 'base/webflow/replaceable_content';
import {
  TextRewritingAi,
  TextRewritingLocation,
} from 'blocks/editor/ai/text_rewriting';
import {
  ElementInstance,
  ElementInstanceState,
  SectionRef,
} from 'blocks/editor/model';
import { ASK_AI_ACTION_POSITION } from 'blocks/editor/selection/frame';
import {
  idForSelectionState,
  SelectionState,
} from 'blocks/editor/selection/states';
import { SelectionStore } from 'blocks/editor/selection/store';
import {
  startRewritingMenu,
  TextRewritingProgress,
} from 'blocks/editor/wireframe/text_rewriting_ui';
import classNames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { expr } from 'mobx-utils';
import React, { useRef } from 'react';
import { AddEffect, NodeDecorator } from './decorators';
import { Interactable, TextEditAction } from './interactable';
import { NodeMeasurements, NodeMeasurerStore } from './node_measuring';
import { measureTextLines } from './text_measuring';
import styles from './text_overrides.css';

export const makeReadonlyTextOverridesDecorator = (
  getTextOverride: (
    id: string,
  ) => ReturnType<typeof getTextOverrideOrSuggestion>,
  getIsPending: (id: string) => boolean,
): NodeDecorator => {
  return function* (node) {
    const replacementType = isReplaceableTextNode(node);
    if (replacementType == null) return;

    const initialValue = textContentsOf(node);
    const override = getTextOverride(node._id);
    const text = override?.text || initialValue;
    yield {
      className: [
        getIsPending(node._id) && styles.isPending,
        override?.type === 'suggestion' && styles.isSuggestion,
      ],
    };

    if (replacementType === TextReplacementType.CHILDREN) {
      const lines = text.split('\n');
      let children =
        lines.length === 1 && text.trim() === '' ? (
          <br />
        ) : (
          lines.map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < lines.length - 1 && <br />}
              {i < lines.length - 1 && <wbr />}
            </React.Fragment>
          ))
        );
      yield { wrapChildren: () => children };
    } else if (replacementType === TextReplacementType.VALUE) {
      yield { attrs: { value: text } };
    } else if (replacementType === TextReplacementType.PLACEHOLDER) {
      yield { attrs: { placeholder: text } };
    } else if (replacementType === TextReplacementType.SELECT_FIRST_OPTION) {
      yield { wrapChildren: () => <option value={text}>{text}</option> };
    } else {
      throw new UnreachableError(replacementType);
    }
  };
};

class TextSuggestionsStore {
  private suggestions = observable.map<string, string>([]);

  addSuggestion(
    instance: ElementInstance,
    nodeId: string,
    text: string,
  ): () => void {
    const key = `${instance.oid}/${nodeId}`;
    this.suggestions.set(key, text);
    return () => {
      if (this.suggestions.get(key) === text) {
        this.suggestions.delete(key);
      }
    };
  }

  getSuggestion(instance: ElementInstance, nodeId: string) {
    const key = `${instance.oid}/${nodeId}`;
    return this.suggestions.get(key);
  }
}

export const globalTextSuggestionsStore = new TextSuggestionsStore();

export function getTextOverrideOrSuggestion(
  element: ElementInstance | ElementInstanceState,
  nodeId: string,
):
  | { type: 'override'; text: string }
  | { type: 'suggestion'; text: string }
  | undefined {
  const suggestion = element.mutable
    ? globalTextSuggestionsStore.getSuggestion(element, nodeId)
    : undefined;
  if (suggestion != null) {
    return { type: 'suggestion', text: suggestion };
  }
  const override = element.textOverrides.get(nodeId);
  if (override != null) {
    return { type: 'override', text: override };
  }
}

export class SectionTextEditingModel {
  constructor(
    readonly selection: SelectionStore,
    readonly section: SectionRef,
    readonly analytics: Pick<AnalyticsService, 'track'>,
    readonly textRewriting: TextRewritingAi,
  ) {}
}

export const makeSelectToEditTextDecorator = (
  model: SectionTextEditingModel,
  measuring: NodeMeasurerStore,
  element: ElementInstance,
  interactables: readonly Interactable[],
): NodeDecorator => {
  const addEffect: AddEffect = measuring.measureEffect(element);
  const interactablesByTextNode = new Map<string, Interactable>();
  for (const ix of interactables) {
    if (ix.text?.element === element) {
      interactablesByTextNode.set(ix.text.nodeId, ix);
    }
  }

  return function* (node) {
    const ix = interactablesByTextNode.get(node._id);
    if (!ix?.text) return;
    const sectionSel = model.selection.factory.forSection(model.section);

    const selectionId = idForSelectionState({
      ...sectionSel,
      element: ix.element,
      nodeId: ix.nodeId,
    });

    const isSelected = expr(() => model.selection.getId() === selectionId);
    const isRewriting = model.textRewriting.isRewriting({
      space: sectionSel.space,
      sref: sectionSel.section,
      element: ix.text.element,
      nodeId: ix.text.nodeId,
    });
    const interactive = globalInteractiveStore.has(selectionId);

    const isEditing = (isSelected && interactive) || isRewriting;

    yield {
      addEffect,
      className: [styles.webflowNode, isEditing && styles.isEditing],
    };
  };
};

function mapMeasuredStyleToOverlayStyle(
  style: CSSStyleDeclaration,
  tagName: string,
): React.CSSProperties {
  return {
    fontFamily: style.fontFamily,
    fontWeight: style.fontWeight,
    fontStyle: style.fontStyle,
    fontSize: style.fontSize,
    letterSpacing: style.letterSpacing,
    lineHeight: style.lineHeight,
    textAlign: style.textAlign as any,
    alignItems: mapVertialAlignToAlignItems(style.verticalAlign, tagName),
    textDecoration: style.textDecoration,
    color: style.color,
  };
}

/** Maps the effect of vertical-align in a block context to align-items in a flexbox context */
function mapVertialAlignToAlignItems(verticalAlign: string, tagName: string) {
  if (tagName.toUpperCase() === 'TEXTAREA') {
    // The user edits the ::placeholder text.
    // Placeholders are always top-aligned, regardless of the vertical-align css.
    return 'flex-start';
  }
  switch (verticalAlign.toLowerCase()) {
    case 'top':
    case 'text-top':
    case 'super':
      return 'flex-start';
    case 'middle':
      return 'center';
    case 'baseline':
      // This isn't quite correct, but results in less jumping around while you
      // add or remove lines in default vertical-align (baseline) elements.
      return 'flex-start';
    default:
      return 'flex-end';
  }
}

export const HIGHLIGHT_TEXT_EDITING_OVERLAYS_CLASSNAME =
  styles.highlightTextEditingOverlays;

type Padding = {
  top: number;
  left: number;
  right: number;
  bottom: number;
};

function addOuterToInnerPadding(
  outer: DOMRect,
  { box: inner, contentScale }: NodeMeasurements,
  add: Padding,
) {
  const scale = 1 / contentScale;
  return {
    // negative padding can not be rendered by css
    top: Math.max(add.top + (inner.top - outer.top) * scale, 0),
    left: Math.max(add.left + (inner.left - outer.left) * scale, 0),
    right: Math.max(add.right + (outer.right - inner.right) * scale, 0),
    bottom: Math.max(add.bottom + (outer.bottom - inner.bottom) * scale, 0),
  };
}

function mapMeasuredPaddingAndBorder(style: CSSStyleDeclaration): Padding {
  return {
    top:
      assertPx(style.paddingTop) +
      assertPx(style.borderTopWidth) * hasBorder(style.borderTopStyle),
    left:
      assertPx(style.paddingLeft) +
      assertPx(style.borderLeftWidth) * hasBorder(style.borderLeftStyle),
    right:
      assertPx(style.paddingRight) +
      assertPx(style.borderRightWidth) * hasBorder(style.borderRightStyle),
    bottom:
      assertPx(style.paddingBottom) +
      assertPx(style.borderBottomWidth) * hasBorder(style.borderBottomStyle),
  };
}

function assertPx(value: string) {
  if (!value) {
    return 0;
  }
  if (!value.endsWith('px')) {
    throw new Error(`Expected px value, got ${value}`);
  }
  return parseFloat(value);
}

function hasBorder(borderStyle: string) {
  return borderStyle === 'none' ? 0 : 1;
}

function PreventFocusWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {children}
    </div>
  );
}

/** Stores which selection IDs are currently double-clicked to edit (i.e. the text box is interactive) */
const globalInteractiveStore = observable.set<string>([], {
  name: 'globalInteractiveStore',
});

/**
 * When the user selects a range, but before they choose a prompt to use,
 * their selection state is stored here.
 **/
const globalRangeToRewrite = observable.box<
  | {
      start: number;
      end: number;
      selectionId: string;
    }
  | undefined
>(undefined);

function getRangeToRewrite(selectionId: string) {
  const v = globalRangeToRewrite.get();
  return v?.selectionId === selectionId ? v : undefined;
}

function resetGlobalRangeToRewrite() {
  globalRangeToRewrite.set(undefined);
}

export const TextNodeEditingOverlay = observer(function _TNEO({
  interactable,
  outerBox,
  action,
  model,
  measuring,
}: {
  interactable: Interactable;
  // The interactable might be a parent node of the actual node we are editing ("action" element)
  //   E.g. we display the interactable around the whole button even if the button text is a
  //        smaller node inside
  outerBox: DOMRect;
  action: TextEditAction;
  model: SectionTextEditingModel;
  measuring: NodeMeasurerStore;
}) {
  const innerMeasurements = measuring.get(action.element, action.nodeId);

  const ref = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<ClickInTextareaRef>(null);

  const sel = {
    ...model.selection.factory.forSection(model.section),
    element: interactable.element,
    nodeId: interactable.nodeId,
  };
  const textRewritingLocation = {
    space: sel.space,
    sref: sel.section,
    element: action.element,
    nodeId: action.nodeId,
  };
  const selectionId = idForSelectionState(sel);
  const isSelected = expr(() => model.selection.getId() === selectionId);

  const rangeToRewrite = getRangeToRewrite(selectionId);
  const allRewritingTasks = model.textRewriting.getAll(textRewritingLocation);
  const rewritingTask = allRewritingTasks[allRewritingTasks.length - 1];

  const interactive = globalInteractiveStore.has(selectionId);
  const setInteractive = React.useCallback(
    (value: boolean) => {
      if (value) {
        globalInteractiveStore.add(selectionId);
      } else {
        // Delay this so that if the user clicks on another text box, it won't change
        // before it focuses the other one.
        setTimeout(() => {
          globalInteractiveStore.delete(selectionId);
        }, 0);
      }
      return value;
    },
    [selectionId],
  );
  const interactiveControlled = React.useMemo(
    () => ({
      value: interactive,
      set: setInteractive,
    }),
    [interactive, setInteractive],
  );
  React.useEffect(
    () => () => void globalInteractiveStore.delete(selectionId),
    [selectionId],
  );

  const override = getTextOverrideOrSuggestion(action.element, action.nodeId);
  const value = override?.text ?? action.originalText ?? '';
  const rewritingMenuItems = innerMeasurements
    ? startRewritingMenu(
        model,
        textRewritingLocation,
        innerMeasurements,
        value,
        rangeToRewrite ?? MAX_RANGE,
      )
    : [];
  const menuOptions = React.useMemo(
    (): MenuDisplayOptions => {
      if (!innerMeasurements) {
        return { search: 'hidden' };
      }
      return {
        menuVariant: 'disjointed',
        search: {
          placeholder: 'Ask AI to write...',
          iconStart: <ColoredIcon color="colorPurple" icon={IconStars} />,
          button: {
            tooltip: 'Use custom prompt',
            onSearch: (search) =>
              model.textRewriting.start(
                innerMeasurements,
                textRewritingLocation,
                value,
                rangeToRewrite ?? MAX_RANGE,
                search,
              ),
          },
        },
      };
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model, innerMeasurements, sel, value, rangeToRewrite],
  );
  const [anchoredMenuPortal] = useAnchoredMenu(
    rangeToRewrite != null && rewritingTask == null,
    resetGlobalRangeToRewrite,
    ref,
    rewritingMenuItems,
    { ...menuOptions, closeOnAnchorMousedown: true },
  );
  const editedTextRef = useRef(false);

  if (!innerMeasurements) return null;
  return (
    <div
      ref={ref}
      className={classNames(
        styles.textEditingOverlay,
        ((isSelected && interactive) || rewritingTask) && styles.isEditing,
        override?.type === 'suggestion' && styles.isSuggestion,
      )}
      style={{
        ...mapMeasuredStyleToOverlayStyle(
          innerMeasurements.style,
          innerMeasurements.node.tag,
        ),
        transform: `scale(${innerMeasurements.contentScale})`,
        transformOrigin: '0 0',
        width: outerBox.width / innerMeasurements.contentScale,
        height: outerBox.height / innerMeasurements.contentScale,
      }}
    >
      <ClickInTextarea
        ref={textareaRef}
        variant="invisible"
        transparentPlaceholder
        padding={addOuterToInnerPadding(
          outerBox,
          innerMeasurements,
          mapMeasuredPaddingAndBorder(innerMeasurements.style),
        )}
        enableClickIn={isSelected}
        fakeSelection={
          override?.type === 'suggestion'
            ? undefined
            : rewritingTask
            ? rewritingTask.range
            : rangeToRewrite
        }
        // Allows Figma-like behavior where the user can (after double clicking into 1 text box)
        // seamlessly select text in other text boxes without having to click into them.
        forceInteractive={!isSelected && globalInteractiveStore.size > 0}
        interactiveControlled={interactiveControlled}
        value={value}
        placeholder={action.originalText}
        placeholderWhenEditing={true}
        allowNewlines={true}
        onChange={(v) => {
          editedTextRef.current = true;
          action.element.textOverrides.set(action.nodeId, v);
        }}
        onBlur={() => {
          if (editedTextRef.current) {
            model.analytics.track({
              event: 'project:copy_update',
              component_slug: action.element.source.slug,
            });
            editedTextRef.current = false;
          }

          const valBefore = getTextOverrideOrSuggestion(
            action.element,
            action.nodeId,
          )?.text;
          let val = valBefore;
          if (val == null) return;
          val = collapseSpacesAndTabs(val);
          if (val.length === 0 || val === action.originalText) {
            // Don't leave greyed out (placeholder) text hanging around
            action.element.textOverrides.delete(action.nodeId);
          } else if (val !== valBefore) {
            // Only apply space collapsing at the end, otherwise the editing experience is weird
            action.element.textOverrides.set(action.nodeId, val);
          }
        }}
      />
      {anchoredMenuPortal}
      {rewritingTask && (
        <TextRewritingProgress
          anchorTo={ref}
          ai={model.textRewriting}
          location={textRewritingLocation}
        />
      )}
    </div>
  );
});

export function startTextRewritingAi(
  sel: SelectionState & { element: ElementInstance },
) {
  globalRangeToRewrite.set({
    ...MAX_RANGE,
    selectionId: idForSelectionState(sel),
  });
}

export function getResetCopyMenuItem(
  interactable: Interactable,
  model: SectionTextEditingModel,
) {
  const action = interactable.text;
  if (!action) return [];
  return [
    menuItem({
      label: 'Reset copy',
      disabled: action.element.textOverrides.get(action.nodeId) == null,
      disabledReason: 'You haven’t changed this text',
      onClick: () => {
        action.element.textOverrides.delete(action.nodeId);
        model.analytics.track({
          event: 'blocks/reset',
          object: 'copy',
          subject: 'node',
          componentSlugs: [action.element.source.slug],
        });
      },
    }),
  ];
}

export async function startEditingText(
  interactable: Interactable,
  model: SectionTextEditingModel,
) {
  if (!interactable.text) return;
  const selectionId = idForSelectionState({
    ...model.selection.factory.forSection(model.section),
    element: interactable.element,
    nodeId: interactable.nodeId,
  });
  // move focus after the menu hides
  setTimeout(() => {
    globalInteractiveStore.add(selectionId);
  }, 0);
}

export function getTextRewritingSelectionAction(
  model: SectionTextEditingModel,
  interactable: Interactable,
) {
  if (!interactable.text) return;
  const sel = {
    ...model.selection.factory.forSection(model.section),
    element: interactable.element,
    nodeId: interactable.nodeId,
  };
  const selectionId = idForSelectionState(sel);
  const textRewritingLocation = {
    space: sel.space,
    sref: sel.section,
    element: interactable.text.element,
    nodeId: interactable.text.nodeId,
  };
  return {
    pos: ASK_AI_ACTION_POSITION,
    content: (
      <TextRewritingButton
        analytics={model.analytics}
        textRewriting={model.textRewriting}
        selectionId={selectionId}
        location={textRewritingLocation}
      />
    ),
    showWhenHovering: false,
    showWhenSelected: true,
  };
}

const TextRewritingButton = observer(function _TextRewiringButton({
  analytics,
  textRewriting,
  selectionId,
  location,
}: {
  analytics: Pick<AnalyticsService, 'track'>;
  textRewriting: TextRewritingAi;
  selectionId: string;
  location: TextRewritingLocation;
}) {
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const onActionClick = React.useCallback(() => {
    // We assume the focused textarea is the textarea for this action
    //   (because this button is only showed when this interactable is selected)
    const el = document.activeElement;
    let range =
      el instanceof HTMLTextAreaElement
        ? { start: el.selectionStart, end: el.selectionEnd }
        : MAX_RANGE;
    if (range.end - range.start <= 0) {
      range = MAX_RANGE; // Select all if nothing is selected
    }
    globalRangeToRewrite.set({ ...range, selectionId });
    analytics.track({
      event: 'project:ask_ai_button_click',
      button_location: 'text_area',
      component_slug: location.element.source.slug,
    });
  }, [analytics, location.element.source.slug, selectionId]);

  const rangeToRewrite = getRangeToRewrite(selectionId);
  const isRewriting = textRewriting.isRewriting(location);
  const isInteractive = globalInteractiveStore.has(selectionId);

  if (!(rangeToRewrite == null && !isRewriting && isInteractive)) return null;

  return (
    <PreventFocusWrapper>
      <Button
        ref={buttonRef}
        onMouseDown={onActionClick}
        label="Ask AI"
        variant="secondary"
        icon={IconStars}
        iconColor="ai"
      />
    </PreventFocusWrapper>
  );
});

/**
 * Emulates an aspects of `whitespace: normal` that we can't achieve in a textarea.
 *
 * See https://developer.mozilla.org/en-US/docs/Web/CSS/white-space
 */
export function collapseSpacesAndTabs(text: string) {
  return text
    .replace(/[\t ]+/g, ' ')
    .replace(/[ ]*\n[ ]*/gm, '\n')
    .replace(/^ /, '')
    .replace(/ $/, '');
}

const MAX_RANGE = { start: 0, end: 1e9 };

export function measureTextOffscreen(
  measurements: NodeMeasurements,
  text: string,
) {
  const { box, contentScale, style, node } = measurements;
  const el = document.createElement('div');
  el.style.position = 'absolute';
  el.style.visibility = 'hidden';
  el.style.top = '0';
  el.style.left = '0';
  const padding = mapMeasuredPaddingAndBorder(style);
  el.style.width = `${
    box.width / contentScale - padding.left - padding.right
  }px`;
  Object.assign(el.style, mapMeasuredStyleToOverlayStyle(style, node.tag));
  el.textContent = text;

  document.body.appendChild(el);
  try {
    return measureTextLines(el);
  } finally {
    document.body.removeChild(el);
  }
}
